<template>
    <Modal class="membership-installments-modal"
           title="MEMBERSHIP_INSTALLMENTS.MODAL_TITLE"
           icon="fa-calendar"
           size="lg"
           :cancel="cancel"
           :ok="ok"
           :ok-spinner="saving"
           ok-label="SAVE">
        <form @submit.prevent="ok">
            <p class="text-center">
                {{ l('MEMBERSHIP_INSTALLMENTS.INTRO', { ref: membership.ref }) }}
            </p>

            <div v-for="subscription in subscriptions"
                 :key="subscription.ID"
                 class="membership-installments-modal__subscription">
                <h4>
                    {{ l('MEMBERSHIP_INSTALLMENTS.SUBSCRIPTION_TITLE') }}
                    {{ dateFilter(subscription.startDate) }}
                    -
                    {{ dateFilter(subscription.endDate) }}
                </h4>

                <div class="membership-installments-modal__amounts">
                    <div>
                        <div>
                            <small v-if="subscription.membershipPlan && subscription.membershipPlan.billingFrequency">
                                {{
                                    l(
                                        'MEMBERSHIP_INSTALLMENTS.CURRENT_AMOUNT_FREQ',
                                        { frequency: subscription.membershipPlan.billingFrequency }
                                    )
                                }}
                            </small>
                            <small v-else>
                                {{
                                    l(
                                        'MEMBERSHIP_INSTALLMENTS.CURRENT_AMOUNT',
                                        { plan: subscription.currentPlan }
                                    )
                                }}
                            </small>
                            {{ currencyFilter(subscription.installmentAmount) }}
                        </div>
                    </div>

                    <div>
                        <div>
                            <i class="fa-solid fa-arrow-right"></i>
                        </div>
                    </div>

                    <div>
                        <div>
                            <label v-if="subscription.membershipPlan && subscription.membershipPlan.billingFrequency"
                                   :for="'new-installment-amount-' + subscription.ID">
                                {{
                                    l(
                                        'MEMBERSHIP_INSTALLMENTS.NEW_AMOUNT_FREQ',
                                        { frequency: subscription.membershipPlan.billingFrequency }
                                    )
                                }}
                            </label>
                            <label v-else
                                   :for="'new-installment-amount-' + subscription.ID">
                                {{
                                    l(
                                        'MEMBERSHIP_INSTALLMENTS.NEW_AMOUNT',
                                        { plan: subscription.currentPlan }
                                    )
                                }}
                            </label>
                            <div class="input-group input-group-lg">
                                <span class="input-group-addon">{{ currencySymbol }}</span>
                                <input :id="'new-installment-amount-' + subscription.ID"
                                       v-model="subscription.newInstallmentAmount"
                                       type="number"
                                       step="0.01"
                                       :name="'new-installment-amount-' + subscription.ID"
                                       class="form-control input-lg financial-amount-input"
                                       @keydown.enter="ok"
                                       @blur="onBlur(subscription)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { dateFilter } from '@/vue/filters/dateFilter';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { preciseRound } from '@/functions/math';

export default {
    components: { Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Membership}
         */
        membership: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            saving: false,

            subscriptions: this.membership.subscriptions,
        };
    },

    computed: {
        /**
         * @return {string}
         */
        currencySymbol() {
            return container.currencyService.getSymbol();
        },
    },

    created() {
        // Set the initial new amounts.
        this.subscriptions.forEach((subscription) => {
            subscription.newInstallmentAmount = subscription.installmentAmount;
        });
    },

    methods: {
        dateFilter,

        ok() {
            // Validate entered amounts.
            for (let i = 0; i < this.subscriptions.length > 0; i++) {
                let newAmount = this.subscriptions[i].newInstallmentAmount;
                newAmount = preciseRound(newAmount, 2);
                if (!(parseFloat(newAmount) > 0)) {
                    container.toastFactory.warningTop('MEMBERSHIP_INSTALLMENTS.INVALID_AMOUNT_ERROR');
                    return;
                }
            }

            this.saving = true;

            container.membershipService.saveNewSubscriptionInstallmentAmounts(
                this.subscriptions,
                (subscriptions, newInstallmentAmounts) => {
                    this.saving = false;
                    let success = true;

                    // Ensure all the new installmentAmounts are saved.
                    for (let i = 0; i < subscriptions.length; i++) {
                        if (subscriptions[i].installmentAmount !== subscriptions[i].newInstallmentAmount) {
                            success = false;
                            break;
                        }
                    }

                    this.resolve({
                        subscriptions: this.subscriptions,
                        success,
                        newInstallmentAmounts,
                    });
                },
            );
        },

        /**
         * Round the amount entered when the user is done typing to prevent things like 0.001
         *
         * @param subscription
         */
        onBlur(subscription) {
            subscription.newInstallmentAmount = preciseRound(subscription.newInstallmentAmount, 2);
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

.membership-installments-modal {

    // This looks like the inverse of each row in the cancel memberships modal

    &__subscription {
        background: @form-group-container-bg;
        border-radius: @border-radius-base;
        /**
         * This padding is purposely excessive so at 1024x768 the 4th item will be half cut off vertically,
         * making it clearer there is more content to scroll down to.
         */
        padding: 15px 10px;
        margin: 10px 0;

        h4 {
            margin: 0 10px 5px;
        }
    }

    &__amounts {
        display: -webkit-flex;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: center;

        > div {
            font-size: 22px;
            min-height: 102px;
            position: relative;
            white-space: normal;
            text-align: center;
            background: rgba(255, 255, 255, .8);
            flex-grow: 1;
            margin: 5px;
            padding: 17px;
            box-shadow: 0 3px 8px -9px black;
            border-radius: @border-radius-base;
            flex-basis: 50%;

            // Vertically center content inside the boxes
            display: flex;
            align-items: center;
            justify-content: center;

            small,
            label {
                display: block;
                font-size: 11px;
            }

            // The arrow between the boxes
            &:nth-child(2) {
                background: none;
                flex-grow: 0;
                flex-basis: 0;
                box-shadow: none;
            }

            // The new amount box
            &:last-child {
                background: @btn-info-active-bg;
                //.btn.active();
                color: @white;

                label {
                    margin-top: 0;
                }
            }

            .input-group {
                width: 100px;
                margin: auto;
            }
        }
    }

}
</style>
